// deployment_class: customer_env_allowed
/**
 * Layer B — the role → home route landing map (Role-View spec §2). A thin,
 * declarative projection: given the authenticated actor's canonical role (from
 * `useAuth().user.role`, surfaced by `/v3/auth/me`), return where that role
 * lands in the app. Rules-as-data (a lookup table, NOT branching logic) per the
 * no-hardcoding doctrine — a new role is ADDED by a table row, never by editing
 * control flow.
 *
 * Typed on `string` (not the backend `Role` union) deliberately: web production
 * code cannot import from the backend `src/v3` (`@v3/*` resolves to web's own
 * `src/*`), and `AuthUser.role` is already `string` for exactly this reason. The
 * companion unit test enumerates every canonical role, so coverage is behavioural
 * rather than compile-time-exhaustive; an unmapped/unknown role falls through to
 * `'/'` — the dashboard is the safe, non-privileged floor (it is itself
 * scope-gated, so a role with no data scopes sees nothing there).
 *
 * Landing targets:
 *   - CEO tier + staff/advisory  → '/'                     (the CEO cockpit)
 *   - Board                      → '/board'                (portal built in Plan 1c)
 *   - Developer / auditor        → '/partnerships'
 *   - Account admin              → '/account/organization' (org self-governance)
 *   - Kathedral internal roles   → '/admin/internal-ops'   (internal build only)
 *   - Marketplace ops            → '/marketplace'
 *   - unassigned (no role yet)   → '/account/profile'      (role-pending state)
 */
const HOME_ROUTE: Readonly<Record<string, string>> = {
  // CEO cockpit — the career CEO, the human advisor (CEO-tier driver, §12), and
  // the PHA staff/other seats all land on the dashboard (scope-filtered).
  pha_ed: '/',
  pha_cfo: '/',
  pha_dev: '/',
  pha_asset_manager: '/',
  pha_staff: '/',
  pha_advisory: '/',
  // Board — its own status-only portal (steel wall); target lands in Plan 1c.
  pha_board: '/board',
  // Developer-side + auditor — partnership surfaces.
  developer_principal: '/partnerships',
  developer_counsel: '/partnerships',
  auditor_external: '/partnerships',
  // Customer self-governance admin.
  account_admin: '/account/organization',
  // Kathedral internal seats — the internal-ops portal (absent from the customer build).
  kathedral_dev_admin: '/admin/internal-ops',
  kathedral_super_admin: '/admin/internal-ops',
  kathedral_platform_admin: '/admin/internal-ops',
  marketplace_operations: '/marketplace',
  // Default-deny floor — no role assigned yet → own profile (role-pending).
  unassigned: '/account/profile',
};

/**
 * The home route for a role. Unknown/unmapped → `'/'` (the dashboard is the safe,
 * non-privileged floor — never a privileged surface).
 */
export function homeRouteFor(role: string): string {
  return HOME_ROUTE[role] ?? '/';
}

/**
 * Every DISTINCT landing target this table can produce, plus the `'/'` fallback
 * `homeRouteFor` returns for an unmapped role.
 *
 * Exists so the reachability test can DERIVE what to check instead of
 * hand-listing roles: a hand-kept list is exactly what rots when a row is added
 * ([[derive-the-exemption-never-hand-author-it]]). A new row here is checked
 * against the real route table automatically, which is the point — `pha_board`
 * pointed at `/board` for weeks while the suite stayed green, because nothing
 * compared a target to a route that exists.
 */
export function allLandingTargets(): string[] {
  return [...new Set([...Object.values(HOME_ROUTE), '/'])];
}
